import styled from "@emotion/styled";
import { Button, Card, Col, Row, Tabs, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { goBack } from "assets/icons/index";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useGetOrderTracksBangbow, useGetOrderTracksSelf, useGetTransportBrands } from "hooks/useOrders";
import TrackTableList from "./TrackTableList";
import SearchTrackForm from "./SearchTrackForm";

const ROW_PER_PAGE_DEFAULT = 20;

export type ShipmentTypeTab = "bangbow" | "self";

const PrintShippingLabel = () => {
  const navigate = useNavigate();
  /** Tab ปัจจุบัน: bangbow = ส่งกับแบรนด์, self = ส่งเอง — บันทึก state ส่งไป API และ component ลูก */
  const [shipmentType, setShipmentType] = useState<ShipmentTypeTab>("bangbow");
  const [orderCode, setOrderCode] = useState<string>("");
  /** เลือกได้หลายแบรนด์ — ส่งไป API เป็น brand_codes (เฉพาะ tab ส่งกับแบรนด์) */
  const [selectedBrandCodes, setSelectedBrandCodes] = useState<string[]>([]);
  /** is_print filter: "" = ทั้งหมด, "true" = ปริ้นแล้ว, "false" = ยังไม่ปริ้น — default ยังไม่ปริ้น */
  const [isPrintFilter, setIsPrintFilter] = useState<string>("false");
  const [refName, setRefName] = useState<string>("");
  const [refTelephone, setRefTelephone] = useState<string>("");
  const [trackingNumber, setTrackingNumber] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [rowPerPage, setRowPerPage] = useState<number>(ROW_PER_PAGE_DEFAULT);
  /** ใช้ trigger refetch หลังกดล้าง — เพราะ setState เป็น async ต้อง refetch หลัง re-render */
  const [clearTrigger, setClearTrigger] = useState(0);

  const { data: brandsData } = useGetTransportBrands();
  const brandList = brandsData?.data ?? [];

  const commonInput = {
    order_code: orderCode || undefined,
    tracking_number: trackingNumber?.trim() || undefined,
    is_print: isPrintFilter === "" ? undefined : isPrintFilter === "true",
    ref_name: refName?.trim() || undefined,
    ref_telephone: refTelephone?.trim() || undefined,
    page_number: pageNumber,
    row_per_page: rowPerPage,
    sort_direction: "desc" as const,
  };

  const { data: dataBangbow, isLoading: isLoadingBangbow, refetch: refetchBangbow } = useGetOrderTracksBangbow({
    ...commonInput,
    brand_codes: selectedBrandCodes.length > 0 ? selectedBrandCodes : undefined,
  });

  const { data: dataSelf, isLoading: isLoadingSelf, refetch: refetchSelf } = useGetOrderTracksSelf(commonInput);

  const data = shipmentType === "bangbow" ? dataBangbow : dataSelf;
  const isLoading = shipmentType === "bangbow" ? isLoadingBangbow : isLoadingSelf;
  const refetch = shipmentType === "bangbow" ? refetchBangbow : refetchSelf;

  const trackList = data?.data ?? [];
  const totalRecord = data?.total_record ?? 0;

  const handlePageChange = useCallback(
    (page: number, pageSize?: number) => {
      setPageNumber(page);
      setRowPerPage(pageSize ?? rowPerPage);
    },
    [rowPerPage]
  );

  const handleSearch = useCallback(() => {
    setPageNumber(1);
    refetch();
  }, [refetch]);

  /** ล้าง filter ทั้งหมดและ reset หน้า — reset state แล้ว trigger refetch เพื่อดึงข้อมูลใหม่ */
  const handleClear = useCallback(() => {
    setOrderCode("");
    setSelectedBrandCodes([]);
    setIsPrintFilter("");
    setRefName("");
    setRefTelephone("");
    setTrackingNumber("");
    setPageNumber(1);
    setClearTrigger((t) => t + 1);
  }, []);

  /** หลังกดล้าง — refetch เมื่อ state อัปเดตแล้ว (ใช้ query key ใหม่) */
  useEffect(() => {
    if (clearTrigger > 0) {
      refetch();
      setClearTrigger(0);
    }
  }, [clearTrigger, refetch]);

  const tabItems = [
    {
      key: "bangbow",
      label: "ส่งกับแบรนด์",
      children: null,
    },
    {
      key: "self",
      label: "ส่งเอง",
      children: null,
    },
  ];

  return (
    <>
      <Card bordered={false} className="criclebox h-full tabs-mobile">
        <HeaderTabs>
          <IconWrapper
            onClick={() => {
              navigate("/setting");
            }}
          >
            <SpanIcon>{goBack}</SpanIcon>
          </IconWrapper>
          <Typography.Title level={5}>พิมพ์ใบปะหน้าพัสดุ</Typography.Title>
          <RightIconWrapper />
        </HeaderTabs>
        <Row className="rowgap-vbox" gutter={[24, 0]}>
          <Col sm={24} md={24}>
            <WrapperAll>
              <StyledTabsWrapper>
                <Tabs
                  activeKey={shipmentType}
                  onChange={(key) => setShipmentType(key as ShipmentTypeTab)}
                  items={tabItems}
                  className="print-label-tabs"
                />
              </StyledTabsWrapper>
              <HeadWrapper>
                <SearchTrackForm
                  orderCode={orderCode}
                  setOrderCode={setOrderCode}
                  trackingNumber={trackingNumber}
                  setTrackingNumber={setTrackingNumber}
                  isPrintFilter={isPrintFilter}
                  setIsPrintFilter={setIsPrintFilter}
                  refName={refName}
                  setRefName={setRefName}
                  refTelephone={refTelephone}
                  setRefTelephone={setRefTelephone}
                  onSearch={handleSearch}
                  onClear={handleClear}
                  showBrandFilter={shipmentType === "bangbow"}
                  brandList={brandList}
                  selectedBrandCodes={selectedBrandCodes}
                  setSelectedBrandCodes={setSelectedBrandCodes}
                />
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  loading={isLoading}
                  onClick={() => refetch()}
                  title="ดึงข้อมูลใหม่ (ใช้ filter เดิม)"
                >
                  Refresh
                </Button>
              </HeadWrapper>
              <CardWrapperTable>
                <TrackTableList
                  data={trackList}
                  loading={isLoading}
                  totalRecord={totalRecord}
                  pageNumber={pageNumber}
                  rowPerPage={rowPerPage}
                  onPageChange={handlePageChange}
                  refetch={refetch}
                  showBrandFilter={shipmentType === "bangbow"}
                  brandList={brandList}
                  selectedBrandCodes={selectedBrandCodes}
                  setSelectedBrandCodes={setSelectedBrandCodes}
                />
              </CardWrapperTable>
            </WrapperAll>
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default PrintShippingLabel;

/** ส่งออกสำหรับให้หน้าอื่นดึงไปใช้: ฟังก์ชัน/ type สร้าง PDF */
export {
  createShippingLabelPdfBase64,
  type ShippingLabelData,
} from "./createShippingLabelPdf";

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const WrapperAll = styled.div`
  padding: 10px 0px 10px 0px;
  background-color: #efefef;
`;

/** สไตล์แท็บ ส่งกับแบรนด์ / ส่งเอง — มินิมอล สีสบายตา */
const StyledTabsWrapper = styled.div`
  margin-bottom: 14px;
  .print-label-tabs.ant-tabs {
    .ant-tabs-nav {
      margin-bottom: 0;
      padding: 0;
    }
    .ant-tabs-nav::before {
      display: none;
    }
    .ant-tabs-nav-list {
      gap: 6px;
    }
    .ant-tabs-tab {
      padding: 8px 18px;
      margin: 0;
      border-radius: 8px;
      font-size: 13px;
      color: #595959;
      font-weight: 500;
      transition: all 0.2s ease;
      background: transparent;
    }
    .ant-tabs-tab:hover {
      color: #262626;
      background: rgba(0, 0, 0, 0.04);
    }
    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: #1677ff;
    }
    .ant-tabs-tab.ant-tabs-tab-active {
      background: rgba(22, 119, 255, 0.08);
    }
    .ant-tabs-ink-bar {
      display: none;
    }
  }
`;

const HeadWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
`;

const CardWrapperTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  background-color: #ffffff;
  padding: 5px;
`;
